import { useState, useEffect } from 'react'
import { AlertCircle, FileText, Loader2 } from 'lucide-react'
import { Modal } from '@atrvd/saas-ui'
import { PaginationControls } from './PaginationControls'
import { formatYMDtoMDY } from './format-utils'
import { useFileImportProjections, useFileImportProjectionsCount } from '@/hooks/useBanking'
import { cn } from '@/lib/utils'
import { DEFAULT_DETAILS_PAGE_SIZE } from '@/types/banking'
import type { FileImportRow } from '@/types/banking'
import { formatCategoryAmount } from '@dcp/shared/utils/formatters'

/**
 * Format a projection's signed amount for display.
 *
 * DCP-250: projections carry a single signed `amount` (positive = inflow,
 * negative = outflow). Positives render green, negatives render red with
 * parentheses (via the shared `formatCategoryAmount` convention). A zero
 * amount is shown in muted style as `$0.00`.
 *
 * @param amount - Signed projection amount (positive = inflow, negative = outflow)
 * @returns Object with formatted `text` and Tailwind `className`
 */
function formatProjectionAmount(amount: number): { text: string; className: string } {
  if (!amount) {
    // Zero (or missing) amount has no flow direction — render muted.
    return { text: '$0.00', className: 'text-muted-foreground' }
  }
  return formatCategoryAmount(amount)
}

/**
 * Modal showing paginated projections for a specific file import.
 * Displays date, category, facility, description, amount, and notes.
 *
 * @param fileImport - The file import record to show projections for (null = closed)
 * @param onClose - Callback to close the modal
 */
export function ProjectionImportDetailsModal({
  fileImport,
  onClose,
}: {
  fileImport: FileImportRow | null
  onClose: () => void
}) {
  const [page, setPage] = useState(0)

  // Reset page when a different import is selected
  useEffect(() => {
    setPage(0)
  }, [fileImport?.id])

  const {
    data: projections,
    isLoading,
    isError,
    error,
  } = useFileImportProjections(
    fileImport?.id ?? null,
    { limit: DEFAULT_DETAILS_PAGE_SIZE, offset: page * DEFAULT_DETAILS_PAGE_SIZE },
  )
  const {
    data: pjTotal = 0,
    isError: isCountError,
    error: countError,
  } = useFileImportProjectionsCount(fileImport?.id ?? null)
  const totalPages = Math.ceil(pjTotal / DEFAULT_DETAILS_PAGE_SIZE)
  // Surface either query's error — see TransactionImportDetailsModal for rationale.
  const hasError = isError || isCountError
  const displayError = error ?? countError

  return (
    <Modal
      isOpen={!!fileImport}
      onClose={onClose}
      title={`Imported Projections — ${fileImport?.file_name ?? ''}`}
      size="xl"
      className="!max-w-[1100px]"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {pjTotal} projection{pjTotal !== 1 ? 's' : ''} imported from this file
        </p>

        {hasError ? (
          <div className="flex flex-col items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p className="text-sm">
              Failed to load projections
              {displayError instanceof Error ? `: ${displayError.message}` : ''}
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !projections?.length ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <FileText className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No projections found</p>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border max-h-[460px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 sticky top-0 z-10">
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Date</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Category</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Facility</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Description</th>
                  <th className="px-3 py-2 text-right font-medium bg-muted/50">Amount</th>
                  <th className="px-3 py-2 text-left font-medium bg-muted/50">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {projections.map((pj) => {
                  const amt = formatProjectionAmount(pj.amount)
                  const dateStr = formatYMDtoMDY(pj.date)

                  return (
                    <tr key={pj.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2 whitespace-nowrap">{dateStr}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{pj.categories?.name ?? '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{pj.facilities?.name ?? '-'}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={pj.description ?? ''}>
                        {pj.description ?? '-'}
                      </td>
                      <td className={cn('px-3 py-2 text-right whitespace-nowrap font-medium', amt.className)}>
                        {amt.text}
                      </td>
                      <td className="px-3 py-2 max-w-[150px] truncate" title={pj.notes ?? ''}>
                        {pj.notes ?? '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </Modal>
  )
}
